import { z } from 'zod';

const Id = z.string().min(1);
const Text = z.string().min(1);

const ThemeMapNodeSchema = z.object({
  id: Id,
  label: Text,
  type: z.enum(['theme', 'talk']),
  size: z.number().nonnegative().optional(),
  speaker: z.string().optional(),
  title: z.string().optional(),
});
const ThemeMapPayloadSchema = z.object({
  nodes: z.array(ThemeMapNodeSchema),
  edges: z.array(
    z.object({
      source: Id,
      target: Id,
      weight: z.number().nonnegative(),
    }),
  ),
});

const ThemeRecordSchema = z.object({
  name: Text,
  prominence_score: z.number().min(0).max(10).default(0),
  evidence_links: z
    .array(
      z.object({
        text: Text,
        paragraph_id: z.string().optional(),
      }),
    )
    .default([]),
});
const ThemeFlowPayloadSchema = z.array(
  z.object({
    talk_id: Id,
    speaker: Text,
    title: Text,
    themes: z.array(ThemeRecordSchema),
  }),
);

const HeatmapPayloadSchema = z.object({
  speakers: z.array(Text),
  themes: z.array(Text),
  grid: z.array(z.array(z.number().min(0).max(10))),
});

const QuoteWallPayloadSchema = z.object({
  quotes: z.array(
    z.object({
      text: Text,
      full_text: z.string().optional(),
      attribution: Text,
      attribution_type: Text,
      speaker: Text,
      talk_title: Text,
      talk_id: Id,
      deep_link: z.string().optional(),
      context: z.string().optional(),
    }),
  ),
});

const NetworkNodeSchema = z.object({
  id: Id,
  label: Text,
  speaker: Text,
  title: Text,
  session: Text,
  primary_theme: z.string().optional(),
});
const NetworkEdgeSchema = z.object({
  source: Id,
  target: Id,
  shared_items: z.array(Text),
  weight: z.number().nonnegative(),
  type: z.enum(['scripture', 'reference']),
});
const TalkNetworkPayloadSchema = z.object({
  nodes: z.array(NetworkNodeSchema),
  edges: z.object({
    scripture: z.array(NetworkEdgeSchema),
    reference: z.array(NetworkEdgeSchema),
    top2: z.array(NetworkEdgeSchema),
  }),
});

const KeywordsPayloadSchema = z.object({
  keywords: z.array(
    z.object({
      word: Text,
      total_count: z.number().int().nonnegative(),
      per_talk: z.record(z.string(), z.number().int().nonnegative()),
    }),
  ),
});

async function loadJson(path: string, conferenceId: string): Promise<unknown> {
  const response = await fetch(`/data/conference/${encodeURIComponent(conferenceId)}/explore/${path}.json`);
  if (!response.ok) {
    throw new Error(
      `Explore data request failed with status ${String(response.status)}.`,
    );
  }
  return response.json();
}

export async function loadThemeMap(conferenceId: string) {
  return ThemeMapPayloadSchema.parse(await loadJson('theme-map', conferenceId));
}

export async function loadThemeFlow(conferenceId: string) {
  return ThemeFlowPayloadSchema.parse(await loadJson('theme-flow', conferenceId));
}

export async function loadHeatmap(conferenceId: string) {
  const payload = HeatmapPayloadSchema.parse(await loadJson('heatmap', conferenceId));
  if (
    payload.grid.length !== payload.speakers.length ||
    payload.grid.some((row) => row.length !== payload.themes.length)
  ) {
    throw new Error('The validated heatmap dimensions do not match.');
  }
  return payload;
}

export async function loadQuoteWall(conferenceId: string) {
  return QuoteWallPayloadSchema.parse(await loadJson('quote-wall', conferenceId));
}

export async function loadTalkNetwork(conferenceId: string) {
  return TalkNetworkPayloadSchema.parse(await loadJson('talk-network', conferenceId));
}

export async function loadKeywords(conferenceId: string) {
  return KeywordsPayloadSchema.parse(await loadJson('keywords', conferenceId));
}

export type ThemeMapPayload = Awaited<ReturnType<typeof loadThemeMap>>;
export type ThemeFlowPayload = Awaited<ReturnType<typeof loadThemeFlow>>;
export type HeatmapPayload = Awaited<ReturnType<typeof loadHeatmap>>;
export type QuoteWallPayload = Awaited<ReturnType<typeof loadQuoteWall>>;
export type TalkNetworkPayload = Awaited<ReturnType<typeof loadTalkNetwork>>;
export type KeywordsPayload = Awaited<ReturnType<typeof loadKeywords>>;
